import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { Store } from '@ngrx/store';
import {
  AiChatActionHandlerService,
  AiChatComponent,
  AiChatPopoutComponent,
} from '@qtek/libs/ai-chat';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { PermissionsService, PermissionType } from '@qtek/libs/permissions';
import { PopoutService } from '@qtek/libs/popout';
import {
  AiChatActions,
  AppState,
  NewRelationsStore,
  selectChatsWithPreview,
  selectCreatedChatIdByMysid,
  TagManagerStore,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { AiChat, Customer, Tag } from '@qtek/shared/models';
import {
  animationFrameScheduler,
  BehaviorSubject,
  fromEvent,
  Subject,
} from 'rxjs';
import {
  auditTime,
  filter,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { TaskListComponent } from '../task-list/task-list.component';

const AI_CHAT_POPOUT_STORAGE_KEY = 'aiChatPopoutState';
const AI_CHAT_POPOUT_RESTORE_KEY = 'aiChatPopoutRestore';
const AI_CHAT_POPOUT_WINDOW_NAME = 'AiChatPopout';
const AI_CHAT_POPOUT_RESTORE_TTL_MS = 5000;

interface AiChatPopoutState {
  chatId: string | null;
  windowId: string;
  startNew: boolean;
  lazySubscribe: boolean;
}

@Component({
  selector: 'qt-right-sidenav',
  templateUrl: './right-sidenav.component.html',
  styleUrls: ['./right-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'right-sidenav',
    '[style.width.px]': 'width()',
  },
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    CommonModule,
    TaskListComponent,
    TranslationCoreModule,
    AiChatComponent,
    MatDividerModule,
  ],
})
export class RightSidenavComponent implements OnDestroy, OnInit, AfterViewInit {
  sidenav = inject(MatSidenav);
  private store = inject<Store<AppState>>(Store);
  private newRelationsStore = inject(NewRelationsStore);
  private tagManagerStore = inject(TagManagerStore);
  private readonly tags$ = toObservable(this.tagManagerStore.tags);
  media = inject(MediaObserver);
  private changeDetectionRef = inject(ChangeDetectorRef);
  private dialogLoader = inject(LazyDialogLoader);
  private aiChatActionHandler = inject(AiChatActionHandlerService);

  @ViewChild('resizeHandle') resizeHandle: ElementRef;
  @Input() mini: boolean;
  @Output() closeSidenav = new EventEmitter<void>();
  @Output() widthChange = new EventEmitter<number>();
  @Output() activeTabChange = new EventEmitter<'chat' | 'tasks'>();
  @Output() chatAvailabilityChange = new EventEmitter<boolean>();
  readonly showChat = signal(false);
  readonly showHistory = signal(false);
  readonly selectedTab = signal<'chat' | 'tasks'>('tasks');
  readonly selectedChatId = signal<string | null>(null);
  readonly chatHistory = signal<AiChat[]>([]);
  /** Number of history items to show; "View more" increases this. */
  readonly historyVisibleCount = signal(10);
  readonly chatSessionKey = signal(this.createWindowId());
  readonly lazyChatSubscribe = signal(false);
  width = signal(400);

  /** Initial slice of chat history for the dropdown list. */
  readonly visibleHistory = computed(() =>
    this.chatHistory().slice(0, this.historyVisibleCount())
  );
  readonly hasMoreHistory = computed(
    () => this.chatHistory().length > this.historyVisibleCount()
  );

  readonly editingChatId = signal<string | null>(null);
  readonly editingTitle = signal('');

  static readonly HISTORY_PAGE_SIZE = 10;

  // Minimum width of the Allo sidenav so header buttons don't get cut off
  private readonly minWidth = 280;
  private readonly maxWidth = 800;
  private router = inject(Router);
  private noteAllTags$ = new BehaviorSubject<Tag[]>([]);
  private mysid = 'right-sidenav-contacts';
  private readonly destroy$ = new Subject<void>();
  private readonly chatFeature = 'ai-agent';
  private permissions = inject(PermissionsService);
  private popoutService = inject(PopoutService);
  private viewContainerRef = inject(ViewContainerRef);
  private lastChatHistoryMysid: string | null = null;
  private ngZone = inject(NgZone);
  private popoutRestored = false;

  private createWindowId(): string {
    return `rs-ai-${Date.now().toString(16)}-${Math.random()
      .toString(16)
      .slice(2, 10)}`;
  }

  constructor() {
    // Sync width signal changes to output event
    effect(() => {
      const currentWidth = this.width();
      this.widthChange.emit(currentWidth);
    });

    // Initial width emit
    setTimeout(() => {
      this.widthChange.emit(this.width());
    });

    this.tagManagerStore.connect({});
    this.tags$
      .pipe(
        filter(tags => !!tags),
        tap(tags => this.noteAllTags$.next(tags as Tag[])),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.store
      .select(selectChatsWithPreview)
      .pipe(takeUntil(this.destroy$))
      .subscribe(chats => {
        const normalized = (chats ?? []).filter(
          (chat): chat is AiChat => !!chat?.id
        );
        this.chatHistory.set(normalized);

        const current = this.selectedChatId();
        if (current && !normalized.some(chat => chat.id === current)) {
          this.selectedChatId.set(null);
        }
      });

    this.permissions
      .isAllowed$(this.chatFeature, PermissionType.View)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAllowed => {
        this.showChat.set(isAllowed);
        this.chatAvailabilityChange.emit(isAllowed);
        if (isAllowed) {
          this.setActiveTab('chat');
          this.requestChatHistoryIfNeeded();
          this.restoreAiChatPopoutIfNeeded();
        }
        if (!isAllowed) {
          this.setActiveTab('tasks');
        }
      });

    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter(event => event.origin === window.location.origin),
        filter(event => event.data?.type === 'ai-chat-popout-state'),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        const state = event.data?.state as AiChatPopoutState | undefined;
        if (!state) return;
        this.saveAiChatPopoutState(state);
        if (this.selectedTab() === 'chat') {
          this.applyPopoutState(state);
        }
      });

    // Handle navigation requests from the AI chat popout window.
    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter(event => event.origin === window.location.origin),
        filter(event => event.data?.type === 'ai-chat-navigate'),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        const url = event.data?.url;
        if (typeof url === 'string' && url.startsWith('/')) {
          this.aiChatActionHandler.navigateToUrl(url);
        }
      });
  }

  ngOnInit(): void {
    this.chatAvailabilityChange.emit(this.showChat());
  }

  ngAfterViewInit(): void {
    this.setupResizeHandle();
  }

  private setupResizeHandle(): void {
    if (!this.resizeHandle?.nativeElement) {
      return;
    }

    const handle = this.resizeHandle.nativeElement;

    // Run event listeners outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      fromEvent<PointerEvent>(handle, 'pointerdown')
        .pipe(
          filter(event => event.button === 0), // Only left mouse button
          switchMap(startEvent => {
            startEvent.preventDefault();
            startEvent.stopPropagation();

            const startX = startEvent.clientX;
            const startWidth = this.width();

            // Create move stream with throttling
            const move$ = fromEvent<PointerEvent>(document, 'pointermove').pipe(
              map(moveEvent => {
                const deltaX = startX - moveEvent.clientX; // Inverted because we're resizing from left
                return Math.max(
                  this.minWidth,
                  Math.min(this.maxWidth, startWidth + deltaX)
                );
              }),
              // Throttle to ~60fps using auditTime - waits for silence then emits latest
              // This ensures smooth updates without overwhelming the CPU
              auditTime(0, animationFrameScheduler),
              // Finalize to ensure cleanup
              finalize(() => {
                if (startEvent.pointerId !== undefined) {
                  handle.releasePointerCapture?.(startEvent.pointerId);
                }
              })
            );

            // Create up stream
            const up$ = fromEvent<PointerEvent>(document, 'pointerup').pipe(
              tap(() => {
                if (startEvent.pointerId !== undefined) {
                  handle.releasePointerCapture?.(startEvent.pointerId);
                }
              })
            );

            // Return move stream until pointer up
            return move$.pipe(takeUntil(up$));
          }),
          takeUntil(this.destroy$)
        )
        .subscribe(newWidth => {
          // Only enter Angular zone when actually updating
          this.ngZone.run(() => {
            this.width.set(newWidth);
          });
        });
    });
  }

  closePanel(): void {
    this.closeSidenav.emit();
  }

  startNewChat(): void {
    if (!this.showChat()) return;
    this.showHistory.set(false);
    this.lazyChatSubscribe.set(true);
    this.chatSessionKey.set(this.createWindowId());
    this.selectedChatId.set(null);
    this.setActiveTab('chat');
    this.requestChatHistoryIfNeeded();
  }

  toggleHistory(): void {
    if (!this.showChat()) return;
    const next = !this.showHistory();
    this.showHistory.set(next);
    if (next) {
      this.historyVisibleCount.set(RightSidenavComponent.HISTORY_PAGE_SIZE);
    }
  }

  showMoreHistory(): void {
    const next =
      this.historyVisibleCount() + RightSidenavComponent.HISTORY_PAGE_SIZE;
    this.historyVisibleCount.set(Math.min(next, this.chatHistory().length));
  }

  startEditTitle(item: AiChat): void {
    this.editingChatId.set(item.id);
    this.editingTitle.set((item.title ?? '').trim());
  }

  onEditingTitleInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.editingTitle.set(value);
  }

  saveEditTitle(): void {
    const id = this.editingChatId();
    const title = this.editingTitle().trim();
    this.editingChatId.set(null);
    this.editingTitle.set('');
    if (!id) return;
    const payload = { id, res: { title: title || undefined } };
    this.store.dispatch(AiChatActions.updateChat({ payload }));
  }

  cancelEditTitle(): void {
    this.editingChatId.set(null);
    this.editingTitle.set('');
  }

  selectChat(chatId: string): void {
    if (!this.showChat()) return;
    this.showHistory.set(false);
    this.lazyChatSubscribe.set(false);
    this.selectedChatId.set(chatId);
    this.chatSessionKey.set(this.createWindowId());
    this.setActiveTab('chat');

    const payload: Parameters<
      typeof AiChatActions.getChatEvents
    >[0]['payload'] = {
      mysid: `chatEvent_${chatId}`,
      prms: { chatId },
    };

    this.store.dispatch(AiChatActions.getChatEvents({ payload }));
  }

  private requestChatHistoryIfNeeded(): void {
    const mysid = `chats_${this.chatSessionKey()}`;
    if (this.lastChatHistoryMysid === mysid) {
      return;
    }

    const payload: Parameters<typeof AiChatActions.getChats>[0]['payload'] = {
      mysid,
    };

    this.store.dispatch(AiChatActions.getChats({ payload }));
    this.lastChatHistoryMysid = mysid;
  }

  openChatInNewWindow(): void {
    if (!this.showChat()) return;

    let chatId = this.selectedChatId();
    // If no chat selected from history, use the chat created for the current session (e.g. new chat with messages)
    if (chatId === null) {
      this.store
        .select(selectCreatedChatIdByMysid(`chats_${this.chatSessionKey()}`))
        .pipe(take(1))
        .subscribe(createdId => {
          chatId = createdId ?? null;
          this.openPopoutWithData(chatId);
        });
    } else {
      this.openPopoutWithData(chatId);
    }
  }

  private openPopoutWithData(chatId: string | null): void {
    const data = {
      context: 'standalone',
      pathLabel: 'standalone/ai-chat',
      popoutType: 'ai-agent',
      chatId,
      windowId: this.chatSessionKey(),
      startNew: chatId === null,
      lazySubscribe: this.lazyChatSubscribe(),
    };

    this.saveAiChatPopoutState({
      chatId,
      windowId: this.chatSessionKey(),
      startNew: chatId === null,
      lazySubscribe: this.lazyChatSubscribe(),
    });

    this.popoutService.openPopoutModal(
      AiChatPopoutComponent,
      data,
      AI_CHAT_POPOUT_WINDOW_NAME,
      this.viewContainerRef
    );

    this.closePanel();
  }

  addNote(): void {
    this.dialogLoader
      .open('note-dialog', {
        mysid: 'right-sidenav',
        refId: '',
        allTags: this.noteAllTags$.getValue(),
      })
      .subscribe();
  }

  addContact(): void {
    this.newRelationsStore.connectPeople({ mysid: this.mysid });
    this.dialogLoader
      .open('customer-dialog', {})
      .pipe(
        tap(() => {
          this.newRelationsStore.disconnectPeople(this.mysid);
        }),
        filter(result => !!result)
      )
      .subscribe((result: Customer) => {
        this.redirectToContactList(btoa(result?.relId));
      });
  }

  public redirectToContactList(id: string): void {
    const targetUrl = `/rels/people/contact/${id}`;
    const prevUrl = this.router.url;

    this.permissions
      .navigateByUrlIfAllowed$(targetUrl, {
        queryParams: { type: 'customer', prevUrl },
      })
      .subscribe();
  }

  ngOnDestroy(): void {
    this.tagManagerStore.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  openTab(tab: 'chat' | 'tasks'): boolean {
    if (tab === 'chat' && !this.showChat()) {
      return false;
    }
    this.setActiveTab(tab);
    if (tab === 'chat') {
      this.syncChatFromPopoutState();
      this.requestChatHistoryIfNeeded();
    }
    return true;
  }

  private setActiveTab(tab: 'chat' | 'tasks') {
    this.selectedTab.set(tab);
    this.activeTabChange.emit(tab);
  }

  private syncChatFromPopoutState(): void {
    const state = this.readAiChatPopoutState();
    if (!state) return;

    this.applyPopoutState(state);
  }

  private applyPopoutState(state: AiChatPopoutState): void {
    const shouldUpdate =
      this.selectedChatId() !== state.chatId ||
      this.lazyChatSubscribe() !== state.lazySubscribe ||
      this.chatSessionKey() !== state.windowId;

    if (!shouldUpdate) return;

    this.chatSessionKey.set(state.windowId);
    this.lazyChatSubscribe.set(state.lazySubscribe);
    this.selectedChatId.set(state.chatId);

    if (state.chatId) {
      const payload: Parameters<
        typeof AiChatActions.getChatEvents
      >[0]['payload'] = {
        mysid: `chatEvent_${state.chatId}`,
        prms: { chatId: state.chatId },
      };

      this.store.dispatch(AiChatActions.getChatEvents({ payload }));
      return;
    }

    if (state.startNew || state.lazySubscribe) {
      this.requestChatHistoryIfNeeded();
    }
  }

  private restoreAiChatPopoutIfNeeded(): void {
    if (this.popoutRestored || !this.showChat()) return;
    this.popoutRestored = true;

    const state = this.readAiChatPopoutState();
    if (!state) return;

    this.chatSessionKey.set(state.windowId);
    this.lazyChatSubscribe.set(state.lazySubscribe);
    this.selectedChatId.set(state.chatId);

    this.markPopoutRestoreInProgress();

    this.popoutService.openPopoutModal(
      AiChatPopoutComponent,
      {
        context: 'standalone',
        pathLabel: 'standalone/ai-chat',
        popoutType: 'ai-agent',
        chatId: state.chatId,
        windowId: state.windowId,
        startNew: state.startNew,
        lazySubscribe: state.lazySubscribe,
      },
      AI_CHAT_POPOUT_WINDOW_NAME,
      this.viewContainerRef
    );
  }

  private saveAiChatPopoutState(state: AiChatPopoutState): void {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(AI_CHAT_POPOUT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage failures (private mode, disabled storage).
    }
  }

  private readAiChatPopoutState(): AiChatPopoutState | null {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(AI_CHAT_POPOUT_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AiChatPopoutState;
    } catch {
      sessionStorage.removeItem(AI_CHAT_POPOUT_STORAGE_KEY);
      return null;
    }
  }

  private markPopoutRestoreInProgress(): void {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(AI_CHAT_POPOUT_RESTORE_KEY, Date.now().toString());
      window.setTimeout(() => {
        try {
          sessionStorage.removeItem(AI_CHAT_POPOUT_RESTORE_KEY);
        } catch {
          // Ignore storage failures during cleanup.
        }
      }, AI_CHAT_POPOUT_RESTORE_TTL_MS);
    } catch {
      // Ignore storage failures (private mode, disabled storage).
    }
  }
}
